import React, { useContext, useState, useImperativeHandle, forwardRef } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "sonner";
import { X } from "lucide-react";

const AddProducts = forwardRef((props, ref) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { GetProducts, adminId, api } = useContext(GlobalContext);

  const [createLoading, setCreateLoading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    firmName: "",
    productName: "",
    gstFile: null,
    panFile: null,
    iconFile: null,
    website: "",
    onboardingURL: "",
  });

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setIsCreateModalOpen(true);
    },
    closeModal: () => {
      setIsCreateModalOpen(false);
    }
  }));

  const uploadImage = async (file, productName, imageType) => {
    if (!file) return null;

    try {
      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });

      const base64Image = await toBase64(file);

      const body = {
        userid: productName,
        item_name: productName.replace(/\s+/g, "_"),
        image_data: base64Image,
        type: imageType,
      };

      const response = await fetch(
        "https://bumppy.in/sales/api/v1/imageUpload.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();

      if (result.status === "0" || result.msg?.includes("Successfully")) {
        return result.image_url;
      } else {
        throw new Error(result.msg || "Upload failed");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      throw error;
    }
  };

  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNewProductFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }
      setNewProduct((prev) => ({
        ...prev,
        [fileType]: file,
      }));
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!adminId) {
      toast.error("Admin ID not found!");
      return;
    }

    setCreateLoading(true);

    try {
      let gstUrl = "";
      let panUrl = "";
      let iconUrl = "";

      if (newProduct.gstFile)
        gstUrl = await uploadImage(
          newProduct.gstFile,
          newProduct.productName,
          "gst"
        );
      if (newProduct.panFile)
        panUrl = await uploadImage(
          newProduct.panFile,
          newProduct.productName,
          "pan"
        );
      if (newProduct.iconFile)
        iconUrl = await uploadImage(
          newProduct.iconFile,
          newProduct.productName,
          "icon"
        );

      const payload = {
        adminId: String(adminId),
        firmName: newProduct.firmName,
        productName: newProduct.productName,
        gst: gstUrl,
        pan: panUrl,
        website: newProduct.website,
        onboardingURL: newProduct.onboardingURL,
        iconURL: iconUrl,
      };

      const res = await api.post(`/Admin/v1.0/createProduct`, payload);

      if (res?.data?.responseCode === "00") {
        toast.success("Product created successfully!");
        await GetProducts();
        setNewProduct({
          firmName: "",
          productName: "",
          gstFile: null,
          panFile: null,
          iconFile: null,
          website: "",
          onboardingURL: "",
        });
        setIsCreateModalOpen(false);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to create product");
      }
    } catch (error) {
      console.error("Create product error:", error);
      toast.error("Something went wrong!");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-blue-400 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                Create New Product
              </h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-white hover:text-gray-200 text-2xl"
              >
                <X />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 outline-none">
                    Firm Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firmName"
                    value={newProduct.firmName}
                    onChange={handleNewProductChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="productName"
                    value={newProduct.productName}
                    onChange={handleNewProductChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={newProduct.website}
                    onChange={handleNewProductChange}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleNewProductFileChange(e, "gstFile")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PAN Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleNewProductFileChange(e, "panFile")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Onboarding URL
                  </label>
                  <input
                    type="text"
                    name="onboardingURL"
                    value={newProduct.onboardingURL}
                    onChange={handleNewProductChange}
                    placeholder="https://example.com/onboarding"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleNewProductFileChange(e, "iconFile")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateProduct}
                  disabled={createLoading}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {createLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    "Create Product"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default AddProducts;
